/**
 * `@rga/proto` — the closed rejection-reason enum.
 *
 * ONE enum covers both halves of a refusal:
 *
 *   0-15   SIM reasons. These are the `INTENT_*` codes from `@rga/sim`'s
 *          `validateIntent`, at the SAME numeric values, so a room can pass a
 *          sim rejection straight to the wire with no mapping table to rot.
 *          `test/sim-compat.test.ts` asserts the equality against the sim.
 *   16+    TRANSPORT reasons. Everything the room refuses before (or instead
 *          of) asking the sim: rate limits, version skew, malformed frames,
 *          shadow bans, session and matchmaking failures.
 *
 * The split point is deliberate: the sim owns 0-15 and may add codes there
 * (PHASE1-REPORT risk 2's `INTENT_ARENA_FULL` is pre-reserved at 7) without
 * ever colliding with a transport code.
 *
 * The enum is CLOSED. A decoder that meets an unknown numeric code reports
 * `REASON_UNKNOWN` rather than inventing one, and the client shows a generic
 * refusal — an unrecognised reason must never be silently treated as OK.
 */

/* -------------------------------------------------- sim reasons (0..15) */

/** Not a rejection. Mirrors `@rga/sim`'s `INTENT_OK`. */
export const REASON_OK = 0;
/** Outside `serverTick - 6 .. serverTick + 2`, as judged by the SIM. */
export const REASON_BAD_TICK = 1;
/** The card is not in one of the four visible hand slots. */
export const REASON_NOT_IN_HAND = 2;
/** The side cannot pay the card's cost right now. */
export const REASON_NOT_ENOUGH_MANA = 3;
/** Out of the deploy territory, or off the board entirely. */
export const REASON_OUT_OF_ZONE = 4;
/** The match has already finished. */
export const REASON_MATCH_OVER = 5;
/** `seq` is not newer than the last one accepted from this side. */
export const REASON_DUPLICATE_SEQ = 6;
/**
 * RESERVED for `@rga/sim`'s `INTENT_ARENA_FULL` (PHASE1-REPORT risk 2: entity
 * store exhausted — the deploy charges mana and produces nothing). The sim does
 * not emit this yet. The code is claimed here so the day it lands, the wire
 * needs no version bump. A room MAY emit it today from its own arena check.
 */
export const REASON_ARENA_FULL = 7;

/* -------------------------------------------- transport reasons (16..) */

/** Too many intents/emotes in the window (§7). Carries `retryAfterMs`. */
export const REASON_RATE_LIMITED = 16;
/**
 * The TRANSPORT's own tick-window check failed before the sim was asked. Kept
 * distinct from `REASON_BAD_TICK` so a room can tell "arrived absurdly late"
 * (a network problem, retry) from "the sim refused the tick" (a client-clock
 * problem, resync).
 */
export const REASON_OUT_OF_TICK_WINDOW = 17;
/** The frame did not decode, or a field was outside its structural bounds. */
export const REASON_MALFORMED = 18;
/** A well-formed frame whose type byte is not in this protocol version. */
export const REASON_UNKNOWN_MESSAGE = 19;
/** `protoVersion` is not one this server speaks. Fatal, refused before start. */
export const REASON_PROTO_VERSION_MISMATCH = 20;
/**
 * The client's card table is not the server's (ADR-0004). Fatal BEFORE the
 * match starts: the client must fetch the server's table and rejoin. A client
 * on a stale — or a newer — table is never silently allowed in.
 */
export const REASON_CARD_TABLE_STALE = 21;
/** The client build is below the server's minimum. Fatal; prompt an update. */
export const REASON_CLIENT_VERSION_UNSUPPORTED = 22;
/**
 * The session is shadow-banned (§7). Sent at most once; subsequent intents are
 * dropped in silence so a probe learns nothing about what tripped the ban.
 */
export const REASON_SHADOW_BANNED = 23;
/** The session is not in a match (or not in THIS match). */
export const REASON_NOT_IN_MATCH = 24;
/** The message claims a side the session does not own. */
export const REASON_NOT_YOUR_SIDE = 25;
/** No such match id. */
export const REASON_MATCH_NOT_FOUND = 26;
/** The resume token did not verify. */
export const REASON_RESUME_TOKEN_INVALID = 27;
/** The 15 s reconnect grace expired; the match was forfeited. */
export const REASON_RESUME_EXPIRED = 28;
/** The matchmaking queue gave up (no opponent inside the widening bands). */
export const REASON_QUEUE_TIMEOUT = 29;
/** The requested region is not serving right now. */
export const REASON_REGION_UNAVAILABLE = 30;
/** The join deck is not 8 distinct, known, deck-legal card ids. */
export const REASON_DECK_INVALID = 31;
/** The session already has a live match; join refused. */
export const REASON_ALREADY_IN_MATCH = 32;
/** The node is draining. Reconnect elsewhere. */
export const REASON_SERVER_SHUTTING_DOWN = 33;
/** The frame exceeded `MAX_MESSAGE_BYTES`. */
export const REASON_PAYLOAD_TOO_LARGE = 34;
/** The server broke. Nothing the client can fix. */
export const REASON_INTERNAL_ERROR = 35;
/**
 * THE IDENTITY IS NOT ALLOWED TO DO THIS — not a bad request, not a bug.
 *
 * Distinct from every code above because the client's correct response is
 * different from all of them: not "retry", not "update", not "fix your deck",
 * but "this account cannot do this and something else has to change first".
 * Its first use is the ranked queue refusing a GUEST identity (build spec §6:
 * a free identity that can be minted per match is a smurf generator, and the
 * ladder pays out real currency at season end). `detail` carries the machine
 * reason; the client renders an i18n key, never this string.
 */
export const REASON_NOT_PERMITTED = 36;
/**
 * The decoder met a reason code it does not know. NEVER sent on the wire — it
 * is what a decoder substitutes, so an unknown code cannot be mistaken for OK.
 */
export const REASON_UNKNOWN = 63;

export type RejectReason =
  | typeof REASON_OK
  | typeof REASON_BAD_TICK
  | typeof REASON_NOT_IN_HAND
  | typeof REASON_NOT_ENOUGH_MANA
  | typeof REASON_OUT_OF_ZONE
  | typeof REASON_MATCH_OVER
  | typeof REASON_DUPLICATE_SEQ
  | typeof REASON_ARENA_FULL
  | typeof REASON_RATE_LIMITED
  | typeof REASON_OUT_OF_TICK_WINDOW
  | typeof REASON_MALFORMED
  | typeof REASON_UNKNOWN_MESSAGE
  | typeof REASON_PROTO_VERSION_MISMATCH
  | typeof REASON_CARD_TABLE_STALE
  | typeof REASON_CLIENT_VERSION_UNSUPPORTED
  | typeof REASON_SHADOW_BANNED
  | typeof REASON_NOT_IN_MATCH
  | typeof REASON_NOT_YOUR_SIDE
  | typeof REASON_MATCH_NOT_FOUND
  | typeof REASON_RESUME_TOKEN_INVALID
  | typeof REASON_RESUME_EXPIRED
  | typeof REASON_QUEUE_TIMEOUT
  | typeof REASON_REGION_UNAVAILABLE
  | typeof REASON_DECK_INVALID
  | typeof REASON_ALREADY_IN_MATCH
  | typeof REASON_SERVER_SHUTTING_DOWN
  | typeof REASON_PAYLOAD_TOO_LARGE
  | typeof REASON_INTERNAL_ERROR
  | typeof REASON_NOT_PERMITTED
  | typeof REASON_UNKNOWN;

/** The highest sim-owned code. Everything above is the transport's. */
export const SIM_REASON_MAX = 15;

/** Stable machine names, for logs, metrics labels and the debug JSON form. */
export const REASON_NAMES: Readonly<Record<RejectReason, string>> = Object.freeze({
  [REASON_OK]: 'ok',
  [REASON_BAD_TICK]: 'bad_tick',
  [REASON_NOT_IN_HAND]: 'not_in_hand',
  [REASON_NOT_ENOUGH_MANA]: 'not_enough_mana',
  [REASON_OUT_OF_ZONE]: 'out_of_zone',
  [REASON_MATCH_OVER]: 'match_over',
  [REASON_DUPLICATE_SEQ]: 'duplicate_seq',
  [REASON_ARENA_FULL]: 'arena_full',
  [REASON_RATE_LIMITED]: 'rate_limited',
  [REASON_OUT_OF_TICK_WINDOW]: 'out_of_tick_window',
  [REASON_MALFORMED]: 'malformed',
  [REASON_UNKNOWN_MESSAGE]: 'unknown_message',
  [REASON_PROTO_VERSION_MISMATCH]: 'proto_version_mismatch',
  [REASON_CARD_TABLE_STALE]: 'card_table_stale',
  [REASON_CLIENT_VERSION_UNSUPPORTED]: 'client_version_unsupported',
  [REASON_SHADOW_BANNED]: 'shadow_banned',
  [REASON_NOT_IN_MATCH]: 'not_in_match',
  [REASON_NOT_YOUR_SIDE]: 'not_your_side',
  [REASON_MATCH_NOT_FOUND]: 'match_not_found',
  [REASON_RESUME_TOKEN_INVALID]: 'resume_token_invalid',
  [REASON_RESUME_EXPIRED]: 'resume_expired',
  [REASON_QUEUE_TIMEOUT]: 'queue_timeout',
  [REASON_REGION_UNAVAILABLE]: 'region_unavailable',
  [REASON_DECK_INVALID]: 'deck_invalid',
  [REASON_ALREADY_IN_MATCH]: 'already_in_match',
  [REASON_SERVER_SHUTTING_DOWN]: 'server_shutting_down',
  [REASON_PAYLOAD_TOO_LARGE]: 'payload_too_large',
  [REASON_INTERNAL_ERROR]: 'internal_error',
  [REASON_NOT_PERMITTED]: 'not_permitted',
  [REASON_UNKNOWN]: 'unknown',
});

const ALL: readonly RejectReason[] = Object.freeze(
  (Object.keys(REASON_NAMES) as readonly string[]).map((k) => Number(k) as RejectReason),
);

/** Every code in the closed enum, ascending. */
export const REJECT_REASONS: readonly RejectReason[] = ALL;

const VALID = new Set<number>(ALL);

/** Is `v` a code in the closed enum? */
export function isRejectReason(v: unknown): v is RejectReason {
  return typeof v === 'number' && VALID.has(v);
}

/**
 * Narrow an arbitrary number from the wire. An unrecognised code becomes
 * `REASON_UNKNOWN`, never `REASON_OK` — a rejection must never decode into an
 * acceptance because a peer is newer than we are.
 */
export function toRejectReason(v: number): RejectReason {
  return VALID.has(v) ? (v as RejectReason) : REASON_UNKNOWN;
}

/** Human-readable name; `unknown(<n>)` for a code outside the enum. */
export function reasonName(v: number): string {
  return isRejectReason(v) ? REASON_NAMES[v] : `unknown(${v | 0})`;
}

/** Was this refusal produced by a sim rule rather than by the transport? */
export function isSimReason(v: RejectReason): boolean {
  return v > REASON_OK && v <= SIM_REASON_MAX;
}

/** Was this refusal produced by the transport rather than by a sim rule? */
export function isTransportReason(v: RejectReason): boolean {
  return v > SIM_REASON_MAX;
}

/**
 * Does this reason end the session? A fatal reason arrives as an
 * `ErrorMessage` with `fatal: true` and the socket closes after it; a
 * non-fatal one is a refused action and the match continues.
 *
 * `REASON_SHADOW_BANNED` is deliberately NOT fatal: the whole point is that
 * the session stays up and the cheat learns nothing.
 */
export function isFatalReason(v: RejectReason): boolean {
  switch (v) {
    case REASON_PROTO_VERSION_MISMATCH:
    case REASON_CARD_TABLE_STALE:
    case REASON_CLIENT_VERSION_UNSUPPORTED:
    case REASON_MATCH_NOT_FOUND:
    case REASON_RESUME_TOKEN_INVALID:
    case REASON_RESUME_EXPIRED:
    case REASON_QUEUE_TIMEOUT:
    case REASON_REGION_UNAVAILABLE:
    case REASON_DECK_INVALID:
    case REASON_ALREADY_IN_MATCH:
    case REASON_SERVER_SHUTTING_DOWN:
    case REASON_PAYLOAD_TOO_LARGE:
    case REASON_INTERNAL_ERROR:
      return true;
    default:
      return false;
  }
}

/**
 * Must the client fetch fresh data and retry, rather than just showing a
 * message? Both cases are ADR-0004's "never silently allowed in".
 */
export function requiresClientRefresh(v: RejectReason): boolean {
  return v === REASON_CARD_TABLE_STALE || v === REASON_CLIENT_VERSION_UNSUPPORTED;
}
